/**
 * VR navigation without gravity ("float"), so VR works in every scene, with or without a floor:
 *
 * - Left thumbstick flies where you look (look up and push forward to climb), X strafes.
 * - Right thumbstick X snap-turns, Y rises and sinks.
 * - Either grip, held, flies three times as fast.
 * - Trigger / pinch aims the engine's teleport arc. It lands on the scene's surfaces (the splat
 *   pick, then the collision raycast) and puts the visitor's feet on the hit; with nothing under
 *   the arc it shows red and does nothing, so nobody lands on an imaginary floor in mid-air. An
 *   arc that slips through a gap in scanned ground lands on the surface it went under, not on a
 *   floater beneath the scene (surfaceAboveMissedArc).
 *
 * - A / X (either hand's lower face button) goes to the next tour stop, B / Y to the previous one
 *   (onStopButton; the viewer teleports the rig there, see vrRigPlacement).
 *
 * Fly speed and teleport reach follow the scene's size, so a room and an aerial scan both work.
 */
// The engine script's types: programs that reach this file through an import (the editor's
// type-check) don't otherwise include them, and a subclass needs its members.
/// <reference path="../playcanvas-scripts.d.ts" />
import * as pc from 'playcanvas';
import { XrNavigation } from 'playcanvas/scripts/esm/xr/xr-navigation.mjs';

/** Gravity of the engine's teleport arc (XrNavigation's ARC_GRAVITY). */
const ARC_GRAVITY = 9.81;
/** Flying speed multiplier while a grip is held. */
export const VR_BOOST = 3;
export const SNAP_TURN_DEGREES = 45;
/** How far in front of a teleport hit the visitor lands, in metres (horizontally). */
export const TELEPORT_STANDOFF = 0.3;

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

export interface VrNavigationTuning {
    /** Top flying speed in m/s with the stick fully over (grip triples it). */
    flySpeed: number;
    /** Teleport arc speed in m/s: a 45° throw lands v²/g away. */
    teleportArcSpeed: number;
    /** Farthest teleport landing from the rig, in metres. */
    maxTeleportDistance: number;
}

/** Fly speed and teleport reach for a scene whose content has this bounding radius (metres). */
export function vrNavigationTuning(sceneRadius: number): VrNavigationTuning {
    const radius = Number.isFinite(sceneRadius) && sceneRadius > 0 ? sceneRadius : 5;
    const reach = clamp(radius * 0.3, 8, 40);
    return {
        flySpeed: clamp(radius * 0.1, 1.5, 12),
        teleportArcSpeed: clamp(Math.sqrt(ARC_GRAVITY * reach), 8, 20),
        maxTeleportDistance: reach * 1.25,
    };
}

/** Thumbstick deflection past the dead zone, rescaled to 0..1 and squared for fine control near the centre. */
export function stickResponse(value: number, deadzone = 0.15): number {
    if (!Number.isFinite(value)) return 0;
    const magnitude = Math.abs(value);
    if (magnitude <= deadzone) return 0;
    const t = Math.min(1, (magnitude - deadzone) / (1 - deadzone));
    return Math.sign(value) * t * t;
}

/** One frame of thumbstick input for float flying. Stick axes as WebXR reports them (forward is -y). */
export interface VrFloatInput {
    /** Left stick X: strafe. */
    moveX: number;
    /** Left stick Y: forward (negative) / back. */
    moveY: number;
    /** Right stick Y: rise (negative) / sink. */
    liftY: number;
    /** A grip is held: fly faster. */
    boost: boolean;
}

/** How fast the velocity eases toward the stick: about 0.3 s to full speed, 0.15 s to a stop. */
const ACCELERATION = 10;
const DECELERATION = 20;

const _target = new pc.Vec3();
const _right = new pc.Vec3();

/**
 * Float flying: eases a velocity toward where the sticks point, so starts and stops never jerk
 * (the main cause of VR discomfort), and returns this frame's rig translation.
 */
export class VrFloatMotion {
    readonly velocity = new pc.Vec3();

    reset(): void {
        this.velocity.set(0, 0, 0);
    }

    /**
     * @param out - Receives this frame's translation.
     * @param headForward - The head's world forward (with pitch: looking up and pushing forward climbs).
     * @param headRight - The head's world right; flattened, so strafing never climbs.
     */
    step(out: pc.Vec3, input: VrFloatInput, headForward: pc.Vec3, headRight: pc.Vec3, speed: number, dt: number): pc.Vec3 {
        const forward = stickResponse(-input.moveY);
        const side = stickResponse(input.moveX);
        const lift = stickResponse(-input.liftY);
        _right.set(headRight.x, 0, headRight.z);
        if (_right.lengthSq() > 1e-8) _right.normalize();
        _target.copy(headForward).mulScalar(forward);
        _target.x += _right.x * side;
        _target.z += _right.z * side;
        _target.y += lift;
        const length = _target.length();
        if (length > 1) _target.mulScalar(1 / length);
        _target.mulScalar(speed * (input.boost ? VR_BOOST : 1));

        const step = Number.isFinite(dt) && dt > 0 ? Math.min(dt, 0.1) : 0;
        const rate = _target.lengthSq() >= this.velocity.lengthSq() ? ACCELERATION : DECELERATION;
        this.velocity.lerp(this.velocity, _target, 1 - Math.exp(-rate * step));
        if (_target.lengthSq() === 0 && this.velocity.lengthSq() < 1e-4) this.velocity.set(0, 0, 0);
        return out.copy(this.velocity).mulScalar(step);
    }
}

/** Fires once per thumbstick flick past `threshold`, re-arming once the stick is back within `reset`. */
export class SnapTrigger {
    private last = 0;

    constructor(private readonly threshold = 0.6, private readonly reset = 0.3) {}

    /** -1 or 1 when a flick fires this frame, else 0. */
    update(value: number): number {
        if (!Number.isFinite(value)) return 0;
        if (this.last !== 0 && Math.abs(value) < this.reset) this.last = 0;
        if (this.last === 0 && Math.abs(value) > this.threshold) {
            this.last = Math.sign(value);
            return this.last;
        }
        return 0;
    }
}

/** Where a teleport lands for an arc hit: pulled back toward the visitor so they stand in front of a wall or statue, not in it. */
export function teleportLanding(out: pc.Vec3, from: pc.Vec3, hit: pc.Vec3, standoff = TELEPORT_STANDOFF): pc.Vec3 {
    const dx = from.x - hit.x, dz = from.z - hit.z;
    const flat = Math.hypot(dx, dz);
    out.copy(hit);
    if (flat > 1e-6) {
        const back = Math.min(standoff, flat);
        out.x += (dx / flat) * back;
        out.z += (dz / flat) * back;
    }
    return out;
}

/** A scene surface pick along a world segment: the first hit between start and end, or null. */
export type SurfacePick = (start: pc.Vec3, end: pc.Vec3) => pc.Vec3 | null;

/** How far above a missed arc point to look for the surface it slipped under, in metres. */
export const UNDER_SURFACE_REACH = 2;
/** A floor this close below a missed arc point means the point is still above the scene, in metres. */
export const UNDER_SURFACE_FLOOR_GAP = 1.5;

const _probeEnd = new pc.Vec3();

/**
 * The surface an arc slipped under, or null. A scan's ground has gaps (a pit's sparse wall, a thin
 * patch), and the arc's segment picks can pass through one and carry on beneath the scene to a
 * floater far below. A descending segment that missed has gone under the scene when its end has a
 * surface just above it and no floor close below it: land on that surface. Under a roof or a table
 * the floor below is close, so those arcs carry on as before.
 */
export function surfaceAboveMissedArc(pick: SurfacePick, start: pc.Vec3, end: pc.Vec3): pc.Vec3 | null {
    if (!(end.y < start.y)) return null;
    _probeEnd.set(end.x, end.y + UNDER_SURFACE_REACH, end.z);
    const hitAbove = pick(end, _probeEnd);
    if (!hitAbove) return null;
    const above = hitAbove.clone(); // the next pick may reuse its result vector
    _probeEnd.set(end.x, end.y - UNDER_SURFACE_FLOOR_GAP, end.z);
    return pick(end, _probeEnd) ? null : above;
}

/** Where the rig goes so the visitor's head lands on a view: its position and heading (`yaw`, degrees). */
export interface VrRigPlacement {
    x: number;
    y: number;
    z: number;
    yaw: number;
}

/**
 * Put the rig so the head lands on a view (a tour stop, an extra waypoint): the head at `eye`
 * horizontally, the view's heading `viewYaw` (degrees about +Y, 0 = facing -Z) in front of the
 * visitor, whatever their own place and turn in the play space (`headLocal`, `headLocalYaw`: the
 * head in rig space). `floorBelowEye` is how far the rig sits below the eyes: the player height in
 * a 'local-floor' space, 0 in a 'local' one.
 */
export function vrRigPlacement(
    eye: { x: number; y: number; z: number },
    viewYaw: number,
    headLocal: { x: number; z: number },
    headLocalYaw: number,
    floorBelowEye: number,
): VrRigPlacement {
    const yaw = viewYaw - headLocalYaw;
    const r = (yaw * Math.PI) / 180;
    const cos = Math.cos(r), sin = Math.sin(r);
    // The head's offset in the play space, turned with the rig (a rotation about +Y).
    const ox = headLocal.x * cos + headLocal.z * sin;
    const oz = -headLocal.x * sin + headLocal.z * cos;
    return { x: eye.x - ox, y: eye.y - floorBelowEye, z: eye.z - oz, yaw };
}

/** Heading (degrees about +Y, 0 = facing -Z) of a direction; NaN when it points straight up or down. */
export function yawOfDirection(direction: { x: number; z: number }): number {
    if (Math.abs(direction.x) < 1e-9 && Math.abs(direction.z) < 1e-9) return NaN;
    return (Math.atan2(-direction.x, -direction.z) * 180) / Math.PI;
}

type GamepadLike = { axes: ReadonlyArray<number>; buttons: ReadonlyArray<{ pressed: boolean }> };

/** xr-standard gamepad buttons: A / X (lower face button) and B / Y (upper). */
const BUTTON_NEXT_STOP = 4;
const BUTTON_PREV_STOP = 5;

/** The tour-stop buttons: each press of A / X (1, next) or B / Y (-1, previous) counts once. */
export class StopButtons {
    /** What each controller held last frame (bit 1: A / X, bit 2: B / Y). */
    private readonly held = new WeakMap<object, number>();

    /** The steps pressed on `controller` since its last update. */
    update(controller: object, pad: Pick<GamepadLike, 'buttons'>): Array<1 | -1> {
        const now = (pad.buttons[BUTTON_NEXT_STOP]?.pressed ? 1 : 0) | (pad.buttons[BUTTON_PREV_STOP]?.pressed ? 2 : 0);
        const before = this.held.get(controller) ?? 0;
        this.held.set(controller, now);
        const steps: Array<1 | -1> = [];
        if (now & 1 && !(before & 1)) steps.push(1);
        if (now & 2 && !(before & 2)) steps.push(-1);
        return steps;
    }
}

const _move = new pc.Vec3();
const _pivot = new pc.Vec3();
const _landing = new pc.Vec3();

/**
 * The engine's XrNavigation with float flying in place of its flat movement and step-wise
 * vertical snaps; its teleport arc stays and lands on the scene (see the file comment).
 */
export class VrFloatNavigation extends XrNavigation {
    static scriptName = 'vrFloatNavigation';

    /** Top flying speed in m/s. */
    flySpeed = 1.5;

    /** The scene surface pick for teleports, or null to land on a plane at the rig's height. */
    surfacePick: SurfacePick | null = null;

    /** Whether surfacePick can answer yet (its index builds after load). */
    surfaceReady: () => boolean = () => false;

    /** A / X pressed (1: next tour stop) or B / Y (-1: previous); null ignores them. */
    onStopButton: ((step: 1 | -1) => void) | null = null;

    /** True while a hand's stick belongs to the VR interface (it scrolls the panel that hand points at). */
    stickCaptured: ((handedness: string) => boolean) | null = null;

    private readonly stopButtons = new StopButtons();

    private readonly motion = new VrFloatMotion();
    private readonly snapTurn = new SnapTrigger();
    private camera: pc.Entity | null = null;

    private readonly castSurface = (start: pc.Vec3, end: pc.Vec3): pc.Vec3 | null => {
        const pick = this.surfacePick;
        if (!pick) return null;
        const hit = pick(start, end) ?? surfaceAboveMissedArc(pick, start, end);
        return hit ? teleportLanding(_landing, this.entity.getPosition(), hit).clone() : null;
    };

    initialize(): void {
        // The engine's thumbstick movement is flat and its vertical moves are steps; we fly.
        this.enableMove = false;
        this.enableSnapVertical = false;
        super.initialize();
        this.camera = (this.entity.findComponent('camera') as pc.CameraComponent | null)?.entity ?? null;
        if (typeof this._currentGroundY !== 'number') {
            console.warn('[StorySplat Viewer] XrNavigation has no _currentGroundY: VR teleport heights may be off');
        }
    }

    /** Scene-size tuning, applied when a VR session starts. */
    configure(tuning: VrNavigationTuning): void {
        this.flySpeed = tuning.flySpeed;
        this.teleportArcSpeed = tuning.teleportArcSpeed;
        this.maxTeleportDistance = tuning.maxTeleportDistance;
        this.motion.reset();
    }

    update(dt: number): void {
        const xr = this.app.xr;
        if (!xr?.active || !this.camera) {
            this.motion.reset();
            return;
        }
        let moveX = 0, moveY = 0, liftY = 0, turnX = 0, boost = false;
        for (const source of xr.input.inputSources) {
            const pad = source.gamepad as GamepadLike | null;
            if (!pad || pad.axes.length < 4) continue;
            if (this.stickCaptured?.(source.handedness)) {
                // Scrolling a panel: no flying or turning with this stick (the buttons still work).
            } else if (source.handedness === 'left') {
                moveX = pad.axes[2];
                moveY = pad.axes[3];
            } else if (source.handedness === 'right') {
                turnX = pad.axes[2];
                liftY = pad.axes[3];
            }
            if (pad.buttons[1]?.pressed) boost = true;
            for (const step of this.stopButtons.update(source, pad)) this.onStopButton?.(step);
        }

        this.motion.step(_move, { moveX, moveY, liftY, boost }, this.camera.forward, this.camera.right, this.flySpeed, dt);
        if (_move.lengthSq() > 0) this.entity.translate(_move);

        const turn = this.snapTurn.update(-turnX);
        if (turn) {
            // Turn about the head, not the rig origin, so the view pivots in place.
            _pivot.copy(this.camera.getLocalPosition());
            this.entity.translateLocal(_pivot);
            this.entity.rotateLocal(0, turn * SNAP_TURN_DEGREES, 0);
            this.entity.translateLocal(_pivot.mulScalar(-1));
        }

        // Teleports land on the scene once its surface can be picked; until then (or with no
        // pick at all) on a plane at the rig's height, the floor the visitor floats on.
        const pickable = !!this.surfacePick && this.surfaceReady();
        this.castRay = pickable ? this.castSurface : null;
        if (!pickable) this.groundHeight = this.entity.getPosition().y;

        super.update(dt);
    }

    tryTeleport(inputSource: pc.XrInputSource): void {
        // Put the feet on the hit. The engine keeps the rig's height above the last surface it
        // landed on, learnt only at initialize and on teleports, so flying (or a VR start at
        // another height) would otherwise lift or sink every landing by that much.
        this._currentGroundY = this.entity.getPosition().y;
        super.tryTeleport(inputSource);
    }
}
